import { useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';

import { useAppDispatch, useAppSelector } from '@verantos-dev/core-annotation-tool';
import type { Concept } from '@verantos-dev/core-annotation-tool';

import { useGetAnnotationWorkspaceQuery } from '../../api/annotationApiSlice';
import {
  setAreAllConceptsExpanded,
  setAnnotationStarted,
  clearAnnotationStarted,
} from '../../state/annotationSlice';
import {
  selectAnnotationStarted,
  selectAreAllConceptsExpanded,
  selectConceptAnnotationIdsInEditMode,
  selectSelectedNoteId,
  selectDirtyAnnotationKeys,
  selectIsAnnotationMutating,
  selectHasUnsavedNewAnnotations,
} from '../../state/selector';
import type { AnnotationWorkspaceResponse } from '../../types/response';

export interface UseAnnotationConfigResult {
  annotationStarted: boolean;
  startAnnotation: () => void;
  selectedNoteId: string | null;
  closeAnnotation: () => void;
  areAllConceptsExpanded: boolean | undefined;
  handleSetAreAllConceptsExpanded: (areAllConceptsExpanded: boolean) => void;
  annotationWorkspaceData: AnnotationWorkspaceResponse | undefined;
  isAnnotationWorkspaceLoading: boolean;
  namesOfConceptsWithoutAnnotationsArray: string[];
  conceptsWithoutAnnotations: Concept[];
  showMarkRemainingNotPresentModal: boolean;
  setShowMarkRemainingNotPresentModal: (showMarkRemainingNotPresentModal: boolean) => void;
  showFinalizedAnnotationModal: boolean;
  setShowFinalizedAnnotationModal: (showFinalizedAnnotationModal: boolean) => void;
  dirtyConceptNames: string[];
  isAnnotationMutating: boolean;
  hasAnnotationsInEditMode: boolean;
  hasUnsavedNewAnnotations: boolean;
}

export const useAnnotationConfig = (): UseAnnotationConfigResult => {
  const selectedNoteId = useAppSelector(selectSelectedNoteId);
  const annotationStarted = useAppSelector(selectAnnotationStarted);
  const areAllConceptsExpanded = useAppSelector(selectAreAllConceptsExpanded);
  const isAnnotationMutating = useAppSelector(selectIsAnnotationMutating);
  const hasUnsavedNewAnnotations = useAppSelector(selectHasUnsavedNewAnnotations);
  const dispatch = useAppDispatch();
  const { taskId } = useParams();

  const {
    data: annotationWorkspaceData,
    isLoading: isAnnotationWorkspaceLoading,
    isFetching: isAnnotationWorkspaceFetching,
  } = useGetAnnotationWorkspaceQuery({ taskId: taskId as string });

  const startAnnotation = () => {
    dispatch(setAnnotationStarted());
  };
  const closeAnnotation = () => {
    dispatch(clearAnnotationStarted());
  };

  const handleSetAreAllConceptsExpanded = (areAllConceptsExpanded: boolean) => {
    dispatch(setAreAllConceptsExpanded(areAllConceptsExpanded));
  };

  // Calculate concepts that have no annotation in the server
  const conceptsWithoutAnnotations = useMemo(() => {
    if (!annotationWorkspaceData?.data?.concepts || !annotationWorkspaceData?.data?.annotations) {
      return [];
    }

    // Get all conceptIds that have annotations
    const conceptIdsWithAnnotations = new Set(
      annotationWorkspaceData.data.annotations.map((annotation) => annotation.conceptId)
    );

    // Return concepts that don't have any annotations (with both id and name)
    return annotationWorkspaceData.data.concepts.filter(
      (concept) => !conceptIdsWithAnnotations.has(concept.conceptId)
    );
  }, [annotationWorkspaceData?.data?.concepts, annotationWorkspaceData?.data?.annotations]);

  const namesOfConceptsWithoutAnnotationsArray = conceptsWithoutAnnotations.map(
    (concept) => concept.conceptName
  );

  const [showMarkRemainingNotPresentModal, setShowMarkRemainingNotPresentModal] =
    useState<boolean>(false);
  const [showFinalizedAnnotationModal, setShowFinalizedAnnotationModal] = useState<boolean>(false);

  const conceptAnnotationIdsInEditMode = useAppSelector(selectConceptAnnotationIdsInEditMode);
  const dirtyAnnotationKeys = useAppSelector(selectDirtyAnnotationKeys);
  const dirtyConceptNames = useMemo(() => {
    return (annotationWorkspaceData?.data?.concepts ?? [])
      .filter((concept) => dirtyAnnotationKeys.some((key) => key.startsWith(concept.conceptId)))
      .map((concept) => concept.conceptName);
  }, [dirtyAnnotationKeys, annotationWorkspaceData?.data?.concepts]);

  return {
    annotationStarted,
    startAnnotation,
    selectedNoteId,
    closeAnnotation,
    areAllConceptsExpanded,
    handleSetAreAllConceptsExpanded,
    annotationWorkspaceData: annotationWorkspaceData?.data,
    isAnnotationWorkspaceLoading: isAnnotationWorkspaceLoading || isAnnotationWorkspaceFetching,
    namesOfConceptsWithoutAnnotationsArray,
    conceptsWithoutAnnotations,
    showMarkRemainingNotPresentModal,
    setShowMarkRemainingNotPresentModal,
    showFinalizedAnnotationModal,
    setShowFinalizedAnnotationModal,
    dirtyConceptNames,
    isAnnotationMutating,
    hasAnnotationsInEditMode: conceptAnnotationIdsInEditMode.length > 0,
    hasUnsavedNewAnnotations,
  };
};
